import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import logo from '../../assets/images/logo.png';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Close menu when location changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.header') && !event.target.closest('.menu-overlay')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  // Optimized toggle function with touch handling
  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  // Close menu function
  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  // Check if link is active
  const isActiveLink = useCallback((path) => {
    return location.pathname === path;
  }, [location.pathname]);

  // Handle logo error
  const handleLogoError = useCallback(() => {
    setLogoError(true);
  }, []);

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="logo-container">
          <Link to="/" className="logo-link" onClick={closeMenu}>
            {!logoError && (
              <img
                src={logo}
                alt="Kasiwaththa Associates Logo"
                className="logo-image"
                onError={handleLogoError}
                loading="eager"
                decoding="async"
              />
            )}
            <span className="logo-text">Kasiwaththa Associates</span>
          </Link>
        </div>

        <div className="header-right">
          {/* Desktop Navigation */}
          <nav className="nav-menu desktop-nav">
            <ul className="nav-list">
              {[
                { to: '/', label: 'Home' },
                { to: '/projects', label: 'Projects' },
                { to: '/blog', label: 'Blog' },
                { to: '/about', label: 'About Us' },
                { to: '/services', label: 'Services' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/downloads', label: 'Downloads' },
                { to: '/careers', label: 'Careers' }
              ].map(({ to, label }) => (
                <li className="nav-item" key={to}>
                  <Link
                    to={to}
                    className={`nav-link ${isActiveLink(to) ? 'active' : ''}`}
                    onClick={closeMenu}
                    aria-label={`Navigate to ${label}`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile Navigation */}
          <nav className={`nav-menu mobile-nav ${isMenuOpen ? 'active' : ''}`}>
            <ul className="nav-list">
              {[
                { to: '/', label: 'Home' },
                { to: '/projects', label: 'Projects' },
                { to: '/blog', label: 'Blog' },
                { to: '/about', label: 'About Us' },
                { to: '/services', label: 'Services' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/downloads', label: 'Downloads' },
                { to: '/careers', label: 'Careers' }
              ].map(({ to, label }) => (
                <li className="nav-item" key={to}>
                  <Link
                    to={to}
                    className={`nav-link ${isActiveLink(to) ? 'active' : ''}`}
                    onClick={closeMenu}
                    aria-label={`Navigate to ${label}`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <button
          className="mobile-menu-button"
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isMenuOpen}
          aria-controls="nav-menu"
          type="button"
        >
          <span className="menu-icon" aria-hidden="true">
            {isMenuOpen ? '✕' : '☰'}
          </span>
        </button>
      </header>

      {/* Menu overlay with proper touch handling */}
      {isMenuOpen && (
        <div
          className="menu-overlay"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* Announce menu state for screen readers */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {isMenuOpen ? 'Navigation menu opened' : 'Navigation menu closed'}
      </div>
    </>
  );
}

export default Header;